import {
  ForwardRefRenderFunction,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState
} from 'react';
import { Button, Form, Image, Input, Row, Typography, Upload } from 'antd';
import { useTranslation } from 'react-i18next';
import { EditOutlined, UploadOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import { GET_PAYMENT_ACCOUNT } from 'gql/profile/queries';
import {
  CREATE_PAYMENT_ACCOUNT,
  UPDATE_PAYMENT_ACCOUNT
} from 'gql/profile/mutations';
import { PaymentAccountDoctor } from 'gql/profile/__generated__/PaymentAccountDoctor';
import { CreatePaymentAccount } from 'gql/profile/__generated__/CreatePaymentAccount';
import { UpdatePaymentAccount } from 'gql/profile/__generated__/UpdatePaymentAccount';

import Signature from '../Signature';
import { showErrorMessage } from 'utils/showErrorMessage';
import { IconButton } from 'components/ui';

const { Text } = Typography;

interface IProps {
  onCompleted(): void;
  onError?(): void;
}

export type PaymentFieldsRef = {
  submit(): void;
  setEditSignature(value: boolean): void;
};

const PaymentFields: ForwardRefRenderFunction<PaymentFieldsRef, IProps> = (
  { onCompleted, onError },
  ref
) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [signature, setSignature] = useState<string | null>('');
  const [error, setError] = useState(false);
  const [editSignature, setEditSignature] = useState(false);
  const { data } = useQuery<PaymentAccountDoctor>(GET_PAYMENT_ACCOUNT, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first'
  });

  const paymentAccount = data?.paymentAccountDoctor?.data;

  const [createPaymentAccount] = useMutation<CreatePaymentAccount>(
    CREATE_PAYMENT_ACCOUNT,
    {
      onCompleted() {
        onCompleted();
      },
      onError: err => {
        showErrorMessage(err);
        onError?.();
      }
    }
  );

  const [updatePaymentAccount] = useMutation<UpdatePaymentAccount>(
    UPDATE_PAYMENT_ACCOUNT,
    {
      onCompleted() {
        onCompleted();
      },
      onError: err => {
        showErrorMessage(err);
        onError?.();
      }
    }
  );

  useEffect(() => {
    if (paymentAccount) {
      form.setFieldsValue({
        account_number: paymentAccount.account_number,
        bank_name: paymentAccount.bank_name,
        billing_address: paymentAccount.billing_address,
        holder_name: paymentAccount.holder_name
      });
      setSignature(paymentAccount.signature);
    }
  }, [form, paymentAccount]);

  const onSubmit = data => {
    if (signature) {
      const input = {
        ...data,
        signature
      };

      if (paymentAccount) {
        return updatePaymentAccount({
          variables: {
            id: paymentAccount?.id,
            input
          }
        });
      }

      createPaymentAccount({
        variables: {
          input
        }
      });
    } else {
      setError(true);
    }
  };

  const handleBeforeUpload = (file: File) => {
    return new Promise<File>(resolve => {
      const reader = new FileReader();

      reader.onloadend = () => {
        const base64String = reader.result as string;
        setSignature(base64String);
        resolve(file);
        setError(false);
      };

      reader.readAsDataURL(file);
    });
  };

  const customRequest = onSuccess => {
    setTimeout(() => {
      onSuccess();
    }, 0);
  };

  useImperativeHandle(ref, () => ({
    submit: () => form.submit(),
    setEditSignature,
    setError
  }));

  return (
    <Form form={form} layout="vertical" onFinish={onSubmit} scrollToFirstError>
      <Form.Item
        label={t('profile.holder_name')}
        name="holder_name"
        messageVariables={{ label: t('profile.holder_name') }}
        rules={[{ required: true }]}
      >
        <Input
          size="large"
          placeholder={t('profile.holder_name_placeholder')}
        />
      </Form.Item>
      <Form.Item
        label={t('profile.bank_name')}
        name="bank_name"
        messageVariables={{
          label: t('profile.bank_name')
        }}
        rules={[{ required: true }]}
      >
        <Input size="large" placeholder={t('profile.bank_name_placeholder')} />
      </Form.Item>
      <Form.Item
        label={t('profile.account_number')}
        name="account_number"
        messageVariables={{
          label: t('profile.account_number')
        }}
        rules={[
          { required: true },
          {
            pattern: /\d+/,
            message: t('validation.only_numbers')
          }
        ]}
      >
        <Input size="large" placeholder="123412341234123" maxLength={40} />
      </Form.Item>
      <Form.Item
        label={t('profile.billing_address')}
        name="billing_address"
        messageVariables={{
          label: t('profile.billing_address')
        }}
        rules={[{ required: true }]}
      >
        <Input
          size="large"
          placeholder={t('profile.billing_address_placeholder')}
        />
      </Form.Item>
      <Row justify="space-between" align="middle">
        <Text style={{ fontWeight: 'bold' }}>{t('profile.signature')}</Text>
        {!!paymentAccount && !editSignature ? (
          <IconButton onClick={() => setEditSignature(true)}>
            <EditOutlined />
          </IconButton>
        ) : (
          <Upload
            multiple={false}
            maxCount={1}
            beforeUpload={handleBeforeUpload}
            customRequest={({ onSuccess }) => customRequest(onSuccess)}
            showUploadList={true}
            name="file"
          >
            <Button icon={<UploadOutlined />}>{t('profile.upload')}</Button>
          </Upload>
        )}
      </Row>
      {!!paymentAccount && !editSignature && !!signature ? (
        <Image src={signature} preview={false} />
      ) : (
        <Signature
          onClear={() => setSignature('')}
          onEnd={image => {
            setSignature(image);
            setError(false);
          }}
        />
      )}

      {error && <Text type="danger">{t('profile.signature_required')}</Text>}
    </Form>
  );
};

export default forwardRef(PaymentFields);
